import { useState, useMemo, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Card,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Flex,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Skeleton,
  Text,
} from '@kognitos/lattice';
import times from 'lodash/times';
import type {
  Bdkv2BookDescriptor,
  V2BookConnectionDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  getIntegrationKey,
  getLatestVersionIntegrations,
  isCreateSupersededBySharedConnection,
} from '../utils';
import { IntegrationCardHeader } from './IntegrationCardHeader';

interface IIntegrationSelectionDialogProps {
  isOpen: boolean;
  integrations: Bdkv2BookDescriptor[];
  /**
   * Existing connections across all books. Each row shows its connection count,
   * and integrations with connections are listed first.
   */
  connections?: V2BookConnectionDescriptor[];
  /**
   * Whether integrations that need no login and already have a shared connection
   * can be picked. When false (the default) they show disabled as "Ready to use",
   * since there is no connection left to create.
   */
  allowSuperseded?: boolean;
  /** Shows placeholder rows while the catalog is still loading. */
  isLoading?: boolean;
  /** Replaces the default "add a connection" description under the title. */
  description?: string;
  onSelect: (integration: Bdkv2BookDescriptor) => void;
  onCancel: () => void;
}

interface IIntegrationRow {
  integration: Bdkv2BookDescriptor;
  label: string;
  connectionCount: number;
  isReadyToUse: boolean;
}

/**
 * Picker for adding an integration: a search field above a scrollable list of
 * the workspace's integrations. Clicking a row selects it.
 */
export const IntegrationSelectionDialog = ({
  isOpen,
  integrations,
  connections,
  allowSuperseded = false,
  isLoading = false,
  description,
  onSelect,
  onCancel,
}: IIntegrationSelectionDialogProps) => {
  const { t: tIntegrations } = useTranslation('integrations');

  const connectionsByBook = useMemo(() => {
    const grouped = new Map<string, V2BookConnectionDescriptor[]>();
    connections?.forEach(connection => {
      const bookName = connection.book_name;
      if (!bookName) return;
      const existing = grouped.get(bookName);
      if (existing) {
        existing.push(connection);
      } else {
        grouped.set(bookName, [connection]);
      }
    });
    return grouped;
  }, [connections]);

  const rows = useMemo<IIntegrationRow[]>(
    () =>
      getLatestVersionIntegrations(integrations)
        .filter(integration => !integration.hidden)
        .map(integration => {
          const bookConnections = connectionsByBook.get(integration.name ?? '');
          return {
            integration,
            label: integration.display_name || integration.name || '',
            connectionCount: bookConnections?.length ?? 0,
            isReadyToUse:
              !allowSuperseded &&
              isCreateSupersededBySharedConnection(
                integration,
                bookConnections,
              ),
          };
        })
        // Pickable integrations with connections first, ready-to-use ones last.
        .sort(
          (a, b) =>
            Number(a.isReadyToUse) - Number(b.isReadyToUse) ||
            Number(b.connectionCount > 0) - Number(a.connectionCount > 0) ||
            a.label.localeCompare(b.label),
        ),
    [integrations, connectionsByBook, allowSuperseded],
  );

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="flex h-[min(40rem,85vh)] flex-col pb-0 sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {tIntegrations('integrations.selectIntegration.title')}
          </DialogTitle>
          <DialogDescription>
            {description ??
              tIntegrations('integrations.selectIntegration.description')}
          </DialogDescription>
        </DialogHeader>
        <IntegrationSearchList
          rows={rows}
          isLoading={isLoading}
          onSelect={onSelect}
        />
      </DialogContent>
    </Dialog>
  );
};

interface IIntegrationSearchListProps {
  rows: IIntegrationRow[];
  isLoading: boolean;
  onSelect: (integration: Bdkv2BookDescriptor) => void;
}

/** Lives inside the dialog content so the search resets each time it opens. */
const IntegrationSearchList = ({
  rows,
  isLoading,
  onSelect,
}: IIntegrationSearchListProps) => {
  const { t: tIntegrations } = useTranslation('integrations');
  const [searchTerm, setSearchTerm] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  const visibleRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      ({ label, integration }) =>
        label.toLowerCase().includes(query) ||
        integration.name?.toLowerCase().includes(query),
    );
  }, [rows, searchTerm]);

  return (
    <Flex
      direction="column"
      align="stretch"
      gap={4}
      className="min-h-0 w-full min-w-0 flex-1"
    >
      <InputGroup>
        <InputGroupAddon>
          <Icon type="Search" size="sm" />
        </InputGroupAddon>
        <InputGroupInput
          autoFocus
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          placeholder={tIntegrations(
            'integrations.selectIntegration.searchPlaceholder',
          )}
          aria-label={tIntegrations(
            'integrations.selectIntegration.searchPlaceholder',
          )}
        />
      </InputGroup>
      {/* Bleeds to the dialog edges so the scrollbar sits at the edge. */}
      <Flex
        direction="column"
        align="stretch"
        gap={2}
        role="group"
        aria-label={tIntegrations('integrations.selectIntegration.title')}
        onScroll={event => setIsScrolled(event.currentTarget.scrollTop > 0)}
        className={cn(
          '-mx-6 min-h-0 flex-1 overflow-y-auto px-6 pt-1 pb-6',
          // The top fade only appears once scrolled, so the first card isn't faded at rest.
          isScrolled
            ? '[mask-image:linear-gradient(to_bottom,transparent,black_2rem,black_calc(100%_-_2rem),transparent)]'
            : '[mask-image:linear-gradient(to_bottom,black_calc(100%_-_2rem),transparent)]',
        )}
      >
        {isLoading ? (
          times(6, index => (
            <Skeleton key={index} className="h-21 shrink-0 rounded-xl" />
          ))
        ) : visibleRows.length === 0 ? (
          <Text color="muted" level="small" className="py-6 text-center">
            {tIntegrations('integrations.selectIntegration.noResults')}
          </Text>
        ) : (
          visibleRows.map(
            ({ integration, label, connectionCount, isReadyToUse }) => (
              <IntegrationChoiceCard
                key={getIntegrationKey(integration)}
                integration={integration}
                label={label}
                connectionCount={connectionCount}
                isReadyToUse={isReadyToUse}
                onSelect={onSelect}
              />
            ),
          )
        )}
      </Flex>
    </Flex>
  );
};

interface IIntegrationChoiceCardProps {
  integration: Bdkv2BookDescriptor;
  label: string;
  connectionCount: number;
  isReadyToUse: boolean;
  onSelect: (integration: Bdkv2BookDescriptor) => void;
}

/** One integration in the picker, laid out like the Integrations tab card header. */
const IntegrationChoiceCard = ({
  integration,
  label,
  connectionCount,
  isReadyToUse,
  onSelect,
}: IIntegrationChoiceCardProps) => {
  const { t: tIntegrations } = useTranslation('integrations');
  const connectedLabel = tIntegrations(
    'integrations.selectIntegration.connectedCount',
    { count: connectionCount },
  );
  const readyLabel = tIntegrations('integrations.selectIntegration.readyToUse');
  const select = () => onSelect(integration);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Card is a div surface, so give it button-like keyboard activation.
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      select();
    }
  };

  if (isReadyToUse) {
    return (
      <Card
        role="button"
        aria-disabled
        aria-label={`${label}, ${readyLabel}`}
        className="shrink-0 gap-0 p-5 opacity-60"
      >
        <IntegrationCardHeader
          integration={integration}
          badge={<Badge variant="secondary">{readyLabel}</Badge>}
        />
      </Card>
    );
  }

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={connectionCount > 0 ? `${label}, ${connectedLabel}` : label}
      onClick={select}
      onKeyDown={handleKeyDown}
      className="focus-ring shrink-0 gap-0 p-5 transition-shadow hover:shadow-md"
    >
      <IntegrationCardHeader
        integration={integration}
        badge={
          connectionCount > 0 && (
            <Badge variant="default" className="tabular-nums">
              {connectedLabel}
            </Badge>
          )
        }
      />
    </Card>
  );
};
